import React from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  RocketLaunch as StartIcon,
  Person as ProfileIcon,
  Business as OrgIcon,
  Inventory as FleetIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../../context/OrganizationContext';
import DroneIcon from '../../components/DroneIcon';
import styles from '../../styles/GettingStarted.module.css';

// Use DroneIcon as FlightIcon for compatibility with existing code
const FlightIcon = DroneIcon;

function GettingStarted() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();
  const { currentOrganization } = useOrganization();
  const isOrgUser = !!currentOrganization;

  const steps = [
    {
      label: 'Create Your Account',
      description: 'Sign up for FlightLoop and verify your email address.',
      icon: <ProfileIcon fontSize="large" color="primary" />,
      action: !isAuthenticated ? 'Sign Up' : 'Completed',
      path: '/register',
      completed: isAuthenticated
    },
    {
      label: 'Complete Your Profile',
      description: 'Add your pilot information, FAA certificate number, and contact details.',
      icon: <ProfileIcon fontSize="large" color="primary" />,
      action: 'Go to Profile',
      path: '/profile',
      completed: false,
      steps: [
        'Enter your FAA Remote Pilot Certificate number',
        'Add your contact information',
        'Upload profile photo (optional)'
      ]
    },
    {
      label: 'Set Up Organization (Optional)',
      description: 'Create or join an organization for team collaboration and fleet management.',
      icon: <OrgIcon fontSize="large" color="primary" />,
      action: isOrgUser ? 'View Organization' : 'Create Organization',
      path: isOrgUser ? '/settings/organization' : '/signup/organization',
      completed: isOrgUser,
      steps: [
        'Choose an organization plan',
        'Enter organization details',
        'Invite team members',
        'Assign roles and permissions'
      ],
      note: 'Organization plans include fleet management, compliance tracking, and analytics.'
    },
    {
      label: 'Add Your Fleet',
      description: 'Register your drones, batteries, and equipment (organization users only).',
      icon: <FleetIcon fontSize="large" color="primary" />,
      action: 'Manage Fleet',
      path: '/fleet/drones',
      completed: false,
      orgOnly: true,
      steps: [
        'Add drones with make, model, and serial numbers',
        'Upload drone photos',
        'Register batteries and track cycle counts',
        'Add payloads and sensors',
        'Log maintenance activities'
      ]
    },
    {
      label: 'Upload Compliance Documents',
      description: 'Upload pilot certificates and set up safety checklists (organization users only).',
      icon: <SecurityIcon fontSize="large" color="primary" />,
      action: 'Manage Compliance',
      path: '/compliance/licenses',
      completed: false,
      orgOnly: true,
      steps: [
        'Upload your Part 107 certificate',
        'Add expiration dates for auto-reminders',
        'Review pre-flight checklist templates',
        'Complete safety checklists before flights'
      ]
    },
    {
      label: 'Submit Your First LAANC Request',
      description: 'Create your first drone operation and get FAA authorization.',
      icon: <FlightIcon fontSize="large" color="primary" />,
      action: 'Create Operation',
      path: '/operations/create',
      completed: false,
      steps: [
        'Select your flight area on the map',
        'Choose operation date and time',
        'Set maximum altitude',
        'Enter operator and drone information',
        'Submit for LAANC authorization',
        'Receive instant approval or wait for manual review'
      ]
    }
  ];

  const handleStepAction = (path) => {
    navigate(path);
  };

  return (
    <Container maxWidth="lg" className={styles.container}>
      <Box sx={{ py: 4 }}>
        <Box className={styles['header-box']} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box className={styles['header-icon']}>
            <StartIcon fontSize="large" color="primary" />
          </Box>
          <Typography variant="h3" component="h1">
            Getting Started with FlightLoop
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          Follow these steps to set up your account and start flying with LAANC authorization.
        </Typography>

        <Divider className={styles['enhanced-divider']} sx={{ my: 4 }} />

        {/* Quick Start Checklist */}
        <Paper elevation={2} className={styles['quick-start-card']} sx={{ p: 3, mb: 4, bgcolor: 'primary.light' }}>
          <Typography variant="h6" gutterBottom>
            Quick Start Checklist
          </Typography>
          <Grid container spacing={2}>
            {steps.filter(step => !step.orgOnly || isOrgUser).map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box className={styles['checklist-item']} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {step.completed ? (
                    <CheckCircle color="success" />
                  ) : (
                    <CheckIcon color="disabled" />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: step.completed ? 600 : 400 }}>
                    {step.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Detailed Steps */}
        <Stepper orientation="vertical">
          {steps
            .filter(step => !step.orgOnly || isOrgUser)
            .map((step, index) => {
            return (
              <Step key={index} active={true} completed={step.completed}>
                <StepLabel
                  icon={step.completed ? <CheckCircle color="success" /> : index + 1}
                  optional={step.orgOnly ? <Typography variant="caption">Organization Only</Typography> : null}
                >
                  <Typography variant="h6">{step.label}</Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ flexShrink: 0, mt: 0.5 }}>
                        {step.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" paragraph>
                          {step.description}
                        </Typography>

                        {step.steps && (
                          <List dense>
                            {step.steps.map((substep, i) => (
                              <ListItem key={i} sx={{ pl: 0 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <CheckIcon fontSize="small" color="action" />
                                </ListItemIcon>
                                <ListItemText primary={substep} />
                              </ListItem>
                            ))}
                          </List>
                        )}

                        {step.note && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                            💡 {step.note}
                          </Typography>
                        )}

                        <Button
                          variant={step.completed ? "outlined" : "contained"}
                          color="primary"
                          className={styles['enhanced-button']}
                          onClick={() => handleStepAction(step.path)}
                          sx={{ mt: 2 }}
                          disabled={step.completed && step.action === 'Completed'}
                        >
                          {step.action}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            );
          })}
        </Stepper>

        {/* Help Section */}
        <Paper elevation={2} className={styles['help-card']} sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Our support team is here to assist you with onboarding and any questions you may have.
          </Typography>
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="outlined"
                className={styles['enhanced-button']}
                onClick={() => navigate('/help/guides')}
              >
                View User Guides
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                className={styles['enhanced-button']}
                onClick={() => navigate('/contactus')}
              >
                Contact Support
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
}

export default GettingStarted;
